//@ts-check
/* global project */
const workspaceApi = require("../backendapi/workspaceApi.js");
const {
    getMembersCountingTowardUsageLimitMap,
    getRolesCountingTowardUsageLimitMap
} = require("../scripts/routes/workspace/utils.js");
const workspaceSvc = require("./workspaceSvc.js");

const _ = require("lodash");

const pendingInvitesMap = {};
const pendingInvitesMapByRole = {};
const pendingJoinRequestsMap = {};
const joinRequestsEnabledMap = {};

function isInviteLimitExceededForExistingWorkspace(emailsToInvite) {
    return emailsToInvite.length > getTotalSeatsRemaining();
}

function inviteLimitReachedForRBACWorkspace() {
    const { adminInfo, labelerInfo, reviewerInfo } = getRBACSeatsRemaining();
    return (
        adminInfo.adminInvitesRemaining +
            labelerInfo.labelerInvitesRemaining +
            reviewerInfo.reviewerInvitesRemaining <=
        0
    );
}

async function validateInviteToken(token) {
    return await workspaceApi.validateInviteToken(token);
}

async function respondToInvite({ action, workspaceId, token }) {
    if (action === "accept") {
        _.setItem("justJoinedWorkspace", workspaceId);
    }

    if (token) return await respondToTokenInvite(token, action);

    return await respondToEmailInvite(workspaceId, action);
}

async function getJoinableWorkspaces() {
    if (window.project.isSSODomain()) return [];

    const workspaces = await workspaceApi.joinableWorkspaces();

    return workspaces.sort((a, b) => {
        if (a.projectCount !== b.projectCount) {
            return b.projectCount - a.projectCount;
        }
        return Object.keys(b.members || {}).length - Object.keys(a.members || {}).length;
    });
}

async function askToJoinWorkspace(workspaceId) {
    if (window.project.isSSODomain()) return;

    return await workspaceApi.createWorkspaceJoinRequest(workspaceId);
}

async function respondToEmailInvite(workspaceId, action) {
    return await workspaceApi.respondToWorkspaceInvite(workspaceId, action);
}

async function respondToTokenInvite(token, action) {
    return await workspaceApi.joinWorkspace(token, action);
}

async function respondToWorkspaceJoinRequest(workspaceId, requestId, role, response) {
    if (window.project.isSSODomain()) return;

    return await workspaceApi.respondToWorkspaceJoinRequest(workspaceId, requestId, role, response);
}

async function getInviteTokens(workspaceId) {
    try {
        return await workspaceApi.getInviteTokens(workspaceId);
    } catch (error) {
        return {};
    }
}

async function getEmailInvites() {
    try {
        return await workspaceApi.getWorkspaceInvites();
    } catch (error) {
        return [];
    }
}

async function loadPendingInvites(workspaceId) {
    try {
        const pendingInvitesByRole = {};
        let invites = await workspaceApi.getPendingWorkspaceInvites(workspaceId);
        invites.map((invite) => {
            if (!invite.role) invite.role = "owner";
            if (!pendingInvitesByRole[invite.role]) {
                pendingInvitesByRole[invite.role] = [];
            }
            const hasInvite = pendingInvitesByRole[invite.role].find((inv) => inv.id === invite.id);
            !hasInvite && pendingInvitesByRole[invite.role].push(invite);
            invite.role =
                invite.roleName ||
                _.roleDisplayName(invite.role, window.workspacesById[window.currentWorkspace]);
        });
        pendingInvitesMapByRole[workspaceId] = pendingInvitesByRole;
        pendingInvitesMap[workspaceId] = invites;
    } catch (error) {
        // Do nothing
    }
}

async function canEnableJoinRequests(workspaceId) {
    try {
        return await workspaceApi.canEnableJoinRequests(workspaceId);
    } catch (error) {
        return false;
    }
}

async function setJoinable(workspaceId, joinable) {
    await workspaceApi.setJoinable(workspaceId, joinable);

    window.workspacesById[workspaceId].joinable = joinable;
}

async function loadPendingJoinRequests(workspaceId) {
    try {
        if (!isJoinableWorkspace(workspaceId)) {
            delete pendingJoinRequestsMap[workspaceId];

            return;
        }

        pendingJoinRequestsMap[workspaceId] =
            await workspaceApi.getPendingJoinRequests(workspaceId);

        return pendingJoinRequestsMap[workspaceId];
    } catch (error) {
        // Do nothing
    }
}

async function loadPendingJoinRequestsForUser() {
    if (window.project.isSSODomain()) return;

    return await workspaceApi.getUserPendingJoinRequests();
}

async function inviteToWorkspace(workspaceId, emails, role, source) {
    const pendingInProcess = emails.map((email) => ({
        to: email,
        role: _.roleDisplayName(role || "owner", window.workspacesById[window.currentWorkspace])
    }));
    let pendingInvites = pendingInvitesMap[workspaceId] || [];
    pendingInvites = pendingInvites.filter((invite) => {
        return !emails.includes(invite.to);
    });
    pendingInvites = pendingInvites.concat(pendingInProcess);
    pendingInvitesMap[workspaceId] = pendingInvites;

    const response = await workspaceApi.inviteToWorkspace({ emails, workspaceId, role, source });
    await loadPendingInvites(workspaceId);
    return response;
}

async function cancelPendingWorkspaceInvite(workspaceId, inviteIdToCancel) {
    const result = await workspaceApi.cancelPendingWorkspaceInvite(workspaceId, inviteIdToCancel);
    await loadPendingInvites(workspaceId);
    return result;
}

function notSupportUserPendingInvites() {
    return (
        pendingInvitesMap?.[window.currentWorkspace]?.filter(
            (invite) => invite.to !== window.project?.supportUser()?.email
        ) || []
    );
}

function getPendingInvites(includeSupport = true) {
    const invites = includeSupport
        ? pendingInvitesMap?.[window.currentWorkspace] || []
        : notSupportUserPendingInvites();
    return _.sortBy(invites, "to");
}

function getPendingJoinRequests() {
    return _.sortBy(pendingJoinRequestsMap[window.currentWorkspace], "requestedAt");
}

function isJoinableWorkspace(workspaceId) {
    if (window.project.isSSODomain()) return false;

    return window.workspacesById[workspaceId]?.joinable;
}

function resolveBaseRole(role) {
    if (["labeler", "reviewer"].includes(role)) return role;
    return "owner";
}

function getRoleBasedSeatsInfo(role) {
    const baseRole = resolveBaseRole(role);
    const wid = window.currentWorkspace;
    const { roles, members } = window.workspacesById[wid];
    const usedRoles = getRolesCountingTowardUsageLimitMap(members, roles);
    const used = Object.keys(usedRoles).filter((uid) => usedRoles[uid] == baseRole).length;
    let limit;
    switch (baseRole) {
        case "owner":
            limit = window.usage?.limit?.collaborators;
            break;
        case "labeler":
            limit = window.usage?.limit?.labelers;
            break;
        case "reviewer":
            limit = window.usage?.limit?.reviewers;
            break;
        default:
            // Customer role edge case, count toward collaborators
            limit = window.usage?.limit?.collaborators;
            break;
    }
    return { used, limit, remaining: limit - used };
}

function getGeneralSeatsRemaining() {
    const wid = window.currentWorkspace;
    const { members: originalMembers } = window.workspacesById[wid];
    const members = getMembersCountingTowardUsageLimitMap(originalMembers);
    const used = Object.keys(members).length;
    const limit = window.usage?.limit?.collaborators;
    return limit - used;
}

function getRBACSeatsRemaining() {
    const adminInfo = getRoleBasedSeatsInfo("owner");
    const labelerInfo = getRoleBasedSeatsInfo("labeler");
    const reviewerInfo = getRoleBasedSeatsInfo("reviewer");
    return {
        adminInfo,
        labelerInfo,
        reviewerInfo,
        adminInvitesRemaining: adminInfo.remaining,
        labelerInvitesRemaining: labelerInfo.remaining,
        reviewerInvitesRemaining: reviewerInfo.remaining
    };
}

function getTotalSeatsRemaining() {
    let remainingSeats = 0;
    if (hasSeparateSeatLimits()) {
        const adminInfo = getRoleBasedSeatsInfo("owner");
        remainingSeats += adminInfo.remaining;
        const labelerInfo = getRoleBasedSeatsInfo("labeler");
        remainingSeats += labelerInfo.remaining;
        const reviewerInfo = getRoleBasedSeatsInfo("reviewer");
        remainingSeats += reviewerInfo.remaining;
    } else {
        remainingSeats = getGeneralSeatsRemaining();
    }
    return remainingSeats;
}

function hasSeparateSeatLimits() {
    return (
        window.usage.plan === "starter" &&
        workspaceSvc.hasRBACAddon() &&
        window.usage.limit.labelers
    );
}

function isBaseRole(role) {
    return ["owner", "labeler", "reviewer"].includes(role);
}

/**
 * Single source of truth for remaining seats for a given role.
 * Used by both UI display (blur validation) and submit validation.
 */
function getSeatsRemainingForRole(role) {
    if (!hasSeparateSeatLimits()) {
        return getTotalSeatsRemaining();
    }
    const baseRole = resolveBaseRole(role);
    const rbacInfo = getRBACSeatsRemaining();
    const remaining = {
        owner: rbacInfo.adminInvitesRemaining,
        labeler: rbacInfo.labelerInvitesRemaining,
        reviewer: rbacInfo.reviewerInvitesRemaining
    };
    return remaining[baseRole] ?? getTotalSeatsRemaining();
}

function validateSeatsLimit(numInvites, role) {
    return numInvites <= getSeatsRemainingForRole(role);
}

module.exports = {
    isInviteLimitExceededForExistingWorkspace,
    inviteToWorkspace,
    loadPendingInvites,
    cancelPendingWorkspaceInvite,
    getEmailInvites,
    getInviteTokens,
    validateInviteToken,
    respondToInvite,
    getJoinableWorkspaces,
    askToJoinWorkspace,
    respondToWorkspaceJoinRequest,
    getPendingInvites,
    canEnableJoinRequests,
    isJoinableWorkspace,
    setJoinable,
    loadPendingJoinRequests,
    loadPendingJoinRequestsForUser,
    getPendingJoinRequests,
    getRoleBasedSeatsInfo,
    getTotalSeatsRemaining,
    getRBACSeatsRemaining,
    inviteLimitReachedForRBACWorkspace,
    hasSeparateSeatLimits,
    validateSeatsLimit,
    getSeatsRemainingForRole
};
